import { Socket, Presence, Channel } from 'phoenix';
import { ServiceSignature } from '../core/_models';
import { useState, useEffect } from 'react';
import { getUserAuth } from '../core/_axios';
import { BASE_WSS_URL } from '../core/_requests';
import { useAuth } from '../AuthContext';

export type ReasonForUpdate = "manually_updated" | "created_by_ipad" | "created_by_qr_code" | "received_raw_input_items" | "received_transcription"
export type ReasonForRemoval = "manually_removed"

export default function useAccountChannel({
    onSignatureUpdated = undefined,
    onSignatureRemoved = undefined,
    onAccountUpdated = undefined,
    onIpadUpdated = undefined,
    accountUniqueId = undefined
}: {
    onSignatureUpdated?: (reasonForUpdate: ReasonForUpdate, signature: ServiceSignature) => void
    onSignatureRemoved?: (reasonForRemoval: ReasonForRemoval, signature: ServiceSignature) => void
    onAccountUpdated?: (account: any) => void
    onIpadUpdated?: (ipad: any) => void
    accountUniqueId?: string | undefined
}) {

    const {
        currentUser,
        viewingAllAccounts,
        currentUserAccountUniqueId,
        currentUserAccountUniqueIds
    } = useAuth()

    const [channel, setChannel] = useState<Channel | null>(null);

    useEffect(() => {
        const socket = new Socket(`${BASE_WSS_URL}/socket/web`, {
            params: {
                web_token: getUserAuth()?.api_token
            }
        });
        
        // Use the provided accountUniqueId if available (for subscribing to actual account channels)
        // Otherwise fall back to the membership-based channel logic
        const channelId = accountUniqueId 
            ? `web:account:${accountUniqueId}`
            : currentUserAccountUniqueId 
                ? `web:account:${currentUserAccountUniqueId}` 
                : `web:all_accounts`;
        
        const updatedChannel = socket.channel(channelId, {
            from_web: true, // maybe a local ID one day?
        });
        updatedChannel.on('updated_signature', (data) => {
            if (!onSignatureUpdated) {
                return
            }
            if (!data.payload?.signature) {
                console.warn('No signature found for this service')
                return
            }
            /*
            if (!data.payload?.reason_for_update) {
                console.warn('No reason for update found for this signature')
                return
            }
            // We used to limit this, but the back-end now can randomly
            // update signatures for various reasons, so we don't want to
            // limit this anymore.
            const allowed_reasons = [
                "manually_updated",
                "created_by_ipad",
                "received_raw_input_items",
                "received_transcription",
                "updated_with_mogul_bridge_sync",
            ]
            if (!allowed_reasons.includes(data.payload.reason_for_update)) {
                console.warn(`Invalid reason for update found for this signature (received: ${data.payload.reason_for_update})`)
                return
            }
            */
            const signature = new ServiceSignature(data.payload.signature)
            if (currentUserAccountUniqueId) {
                // Confirm that the signature is for the current account
                if (signature.service.account.unique_identifier !== currentUserAccountUniqueId) {
                    // TODO: Fix this in the backend, this is a temporary hack
                    console.warn('Signature update received for a different account, ignoring')
                    return
                }
                onSignatureUpdated(
                    data.payload.reason_for_update as ReasonForUpdate,
                    signature
                )
            } else {
                // Confirm that the signature is for one of the accounts we are viewing
                // Allow super super users to see everything and agency admins to 
                // only see their own agency accounts.
                if (currentUser.is_super_super_user) {
                    // Do nothing for now.
                } else if (currentUser.belongs_to_agency && currentUser.is_agency_admin) {
                    if (signature.service.account.belongs_to_agency?.unique_identifier !== currentUser.belongs_to_agency?.unique_identifier) {
                        console.warn('Signature update received for an account not in the agency, ignoring')
                        return
                    }
                } else {
                    if (!currentUserAccountUniqueIds.includes(signature.service.account.unique_identifier)) {
                        // TODO: Fix this in the backend, this is a temporary hack
                        console.warn('Received signature update for an account not being viewed, ignoring')
                        return
                    }
                }
                onSignatureUpdated(
                    data.payload.reason_for_update as ReasonForUpdate,
                    signature
                )
            }
        })
        updatedChannel.on('removed_signature', (data) => {
            if (!onSignatureRemoved) {
                return
            }
            if (!data.payload?.signature) {
                console.warn('No signature found for this service')
                return
            }
            const signature = new ServiceSignature(data.payload.signature)
            onSignatureRemoved(
                data.payload.reason_for_removal as ReasonForRemoval,
                signature
            )
        })
        updatedChannel.on('account_updated', (data) => {
            if (!onAccountUpdated) {
                return
            }
            if (!data.account) {
                console.warn('No account data found in update')
                return
            }
            onAccountUpdated(data.account)
        })
        updatedChannel.on('ipad_updated', (data) => {
            if (!onIpadUpdated) {
                return
            }
            if (!data.payload?.ipad) {
                console.warn('No iPad data found in update')
                return
            }
            onIpadUpdated(data.payload.ipad)
        })
        setChannel(updatedChannel)
        const presence = new Presence(updatedChannel)
        socket.connect();
        presence.onSync(() => {
            presence.list((id, { metas: metaItems }) => {
                // todo
            })
        })
        updatedChannel.join()
            .receive("ok", resp => {
                if (resp.connection_error) {
                    console.error("Connection error", resp.connection_error)
                    return;
                }
            })
            .receive("error", resp => {

            })
        return () => {
            updatedChannel.leave()
            socket.disconnect()
        }
    }, [viewingAllAccounts, currentUserAccountUniqueId, currentUserAccountUniqueIds, accountUniqueId]);

    return {
        channel
    }
}